<script lang="ts">
    import WeightsPage from '@Criteria/Weights/Pages/WeightsPage'
    export default WeightsPage
</script>

<template>
    <div>
        <WeightsSetDependenciesViewer :viewModel="weightsSetDependenciesViewer" />

        <WeightsSetEditor :viewModel="weightsSetEditor"
                          @normalize-weights="handleNormalizeWeights"
                          @create-weights-set="handleCreateWeightsSetCommand"
                          @update-weights-set="handleUpdateWeightsSetCommand" />

        <CriteriaSurveyAccessLinks :viewModel="criteriaSurveyAccessLinks"
                                   @show-email-sender="showCriteriaSurveyEmailSender"
                                   @download-links="handleDownloadAccessLinks" />

        <CriteriaSurveyEmailSender :viewModel="criteriaSurveyEmailSender" 
                                   @send-email="handleSendEmailCommand" />

        <SubSurveysManagerDialog :viewModel="subSurveysManagerDialog"
                                 @include-sub-survey="handleIncludeSubSurveyCommand"
                                 @exclude-sub-survey="handleExcludeSubSurveyCommand"
                                 @change-vote-strength="handleChangeSubSurveyVoteStrengthCommand" />

        <SurveyEditorDialog :viewModel="surveyEditorDialog"
                            @delete-survey="handleDeleteSurveyCommand"
                            @create-survey="handleCreateSurveyCommand"
                            @update-survey="handleUpdateSurveyCommand" />

        <SurveyOpenerDialog :propOpened="surveyOpenerSurveyId != null" :propSurveyId="surveyOpenerSurveyId"
                            @start-survey="handleStartSurveyCommand"
                            @show-access-links="showAccessLinks"
                            @close-dialog="surveyOpenerSurveyId = null" />

        <SurveyCloserDialog :propOpened="surveyCloserSurveyId != null" :propSurveyId="surveyCloserSurveyId"
                            @stop-survey="handleStopSurveyCommand"
                            @close-dialog="surveyCloserSurveyId = null" />

        <WeightsComparerDialog :propOpened="weightsComparerOpened" :propRm="weightsComparer"
                               @close-dialog="weightsComparerOpened = false" />

        <WeightsViewerDialog :propOpened="weightsViewerOpened === true" :propRm="weightsViewer"
                             @export-weightsset-weights="handleExportWeightsToXlsFromWeightsSetCommand"
                             @export-survey-weights="handleExportWeightsToXlsFromSurveyVotesCommand"
                             @close-dialog="weightsViewerOpened = false" />


        <WeightsSetDeleter :viewModel="weightsSetDeleter" 
                           @delete-weights-set="handleDeleteWeightsSetCommand" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <h3 v-if="!rm"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>
            <div v-if="rm">
                <a class="btn btn-outline-secondary float-right" href="https://www.transparentchoice.com/user-guide/weight-criteria" title="User guide: Criteria weights" target="_blank">
                    <i class="fa fa-info" aria-hidden="true"></i>
                </a>
                <h3>Goal: {{rm.GoalName}}</h3>
                <br />
                <GoalNavigation ActivePage="criteria-surveys" :WorkspaceId="workspaceId" :ProjectId="projectId" :EvalModelId="evalModelId" :ScoringEnabled="rm.ScoringEnabled" />

                <div class="dropdown d-inline-block mr-1">
                    <button class="btn btn-success dropdown-toggle" type="button" data-toggle="dropdown">
                        Create
                    </button>
                    <div class="dropdown-menu">
                        <a class="dropdown-item" @click="showSurveyCreator">Opinion panel</a>
                        <a class="dropdown-item" @click="showWeightsSetCreator">Weights set</a>
                        <a class="dropdown-item" v-if="!rm.ScoringEnabled" v-sanitizedHref="url_CreateWeightedScorePage">Weighted score</a>
                    </div>
                </div>

                <div class="dropdown d-inline-block">
                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                        Export to xls
                    </button>
                    <div class="dropdown-menu">
                        <a class="dropdown-item" @click="DownloadVotesCommand">Votes</a>
                        <a class="dropdown-item" @click="handleExportAllWeightsToXlsCommand">Weights</a>
                    </div>
                </div>

                <br />
                <br />

                <div class="row">
                    <div class="col-lg-4 col-md-6 col-sm-6 col-xs-12" v-for="s in surveysView" :key="s.Id">
                        <div class="card mt-2">
                            <div class="card-body">
                                <h5 class="card-title">
                                    <i class="fa fa-lock text-danger" title="Survey is closed" v-if="!s.SurveyStarted"></i>
                                    <i class="fa fa-unlock-alt text-success" title="Survey is open" v-else></i>
                                    {{s.Name}}
                                </h5>
                                <p class="card-text">
                                    <a class="text-primary" v-sanitizedHref="url_CriteriaPanelistsPage(s)">
                                        Panelists: {{s.RespondentsCount}}
                                    </a>
                                    <br />
                                    <a class="text-primary" @click="showSubSurveysManager(s.Id)">
                                        Combined panels: {{s.SubSurveysCount}}
                                    </a>
                                </p>

                                <a v-sanitizedHref="url_CriteriaMeetingManagerPage(s)" class="btn btn-sm btn-outline-secondary mr-1">Review</a>

                                <div class="dropdown d-inline-block">
                                    <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu">
                                        <a class="dropdown-item" @click="showSurveyEditor(s.Id)">Edit</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" v-if="!s.SurveyStarted" @click="showSurveyOpener(s.Id)">Open survey</a>
                                        <a class="dropdown-item" v-if="s.SurveyStarted" @click="showSurveyCloser(s.Id)">Close survey</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="showSurveyWeightsViewer(s.Id)">View weights</a>
                                        <a class="dropdown-item" @click="showSurveyWeightsComparer(s.Id)">Compare weights</a>
                                        <a class="dropdown-item" @click="showSurveyWeightsSetCreator(s)">Create weights set</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="showSurveyDeleter(s.Id)">Delete</a>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <br />
                <br />

                <button role="button" class="btn btn-outline-secondary mb-1" :disabled="selectedWeightsSets.length === 0" @click="showWeightsSetsWeightsComparer">
                    Compare ({{selectedWeightsSets.length}})
                </button>
                <table class="table table-bordered table-hover table-browser">
                    <thead>
                        <tr>
                            <th style="width: 30px;">

                            </th>
                            <th>
                                <span>Weights set</span>
                            </th>
                            <th style="width: 180px;">
                                <span>Updated on</span>
                            </th>
                            <th style="width: 180px;">

                                <span>Created on</span>
                            </th>
                            <th style="width: 90px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="ws in weightsSetsView" :key="ws.Id">
                            <td style="width: 30px;">
                                <div class="form-check form-check-inline mr-0">
                                    <input class="form-check-input mr-0" type="checkbox" v-model="ws.IsSelected">
                                </div>
                            </td>
                            <td>
                                <a class="text-dark" @click="showWeightsSetEditor(ws)">
                                    {{ws.Name}} <span v-if="ws.Id === defaultWeightsSetId" class="badge badge-secondary">Default</span>
                                </a>
                            </td>
                            <td class="text-center">
                                {{formatDate(ws.Modified)}}<br />
                                <small>{{formatTime(ws.Modified)}}</small>
                            </td>
                            <td class="text-center">
                                {{formatDate(ws.Created)}}<br />
                                <small>{{formatTime(ws.Created)}}</small>
                            </td>
                            <td>
                                <div class="btn-group">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions <span class="caret"></span>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showWeightsSetEditor(ws)">Edit</a>
                                        <a class="dropdown-item" @click="showWeightsSetWeightsViewer(ws.Id)">View weights</a>
                                        <a class="dropdown-item" @click="handleChangeDefaultWeightsSetCommand(ws.Id)">Set as default</a>
                                        
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="showWeightsSetDependencies(ws)">View dependencies</a>
                                        <a class="dropdown-item" @click="showWeightsSetDeleter(ws)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

            </div>
        </div>
    </div>
</template>
